import type {
  KnowledgeArticleRequest,
  KnowledgeArticleResponse,
} from 'src/api/ProactApiClient';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';

export const KnowledgeBaseDocumentTitle = 'Knowledge Base';

// Tags persist as a comma-separated string on the entity but are edited/viewed
// as a string[]; these split/join at the mapper boundary only.
export const splitKnowledgeArticleTags = (tags?: string): string[] =>
  tags
    ? tags
        .split(',')
        .map(tag => tag.trim())
        .filter(Boolean)
    : [];

// The global-article toggle is appended only for super admins (hidden, not
// disabled, for everyone else) — global authoring is super-admin-only.
export const KnowledgeArticleFormMeta = (
  isSuperAdmin: boolean
): IFormFieldV2[] => [
  {
    name: 'title',
    label: 'Title',
    type: 'text',
    required: true,
    validation: {maxLength: 255},
    size: 'md',
  },
  {
    name: 'tags',
    label: 'Tags',
    type: 'tags',
    required: false,
    description: 'Tags used to surface this article in search.',
  },
  {
    name: 'body',
    label: 'Body',
    type: 'richtext',
    required: true,
  },
  {
    name: 'published',
    label: 'Published',
    type: 'switch',
    required: false,
    description:
      'Published articles are visible to readers and used by the assistant.',
    switchOptions: {
      positiveText: 'Published',
      negativeText: 'Draft',
    },
  },
  ...(isSuperAdmin
    ? ([
        {
          name: 'isGlobal',
          label: 'Global Article',
          type: 'switch',
          required: false,
          description:
            'Global articles are visible to every instance. Only super admins may author them.',
          switchOptions: {
            positiveText: 'Global',
            negativeText: 'This instance only',
          },
        },
      ] as IFormFieldV2[])
    : []),
];

export const KnowledgeArticleViewMeta: IFormFieldV2[] = [
  {
    name: 'body',
    label: 'Body',
    type: 'richtext',
  },
];

// Splits the entity's comma-separated tags into the string[] the 'tags' field
// renders; resetForm otherwise seeds '' and desyncs the controlled input.
export const KnowledgeArticleToFormData = (
  entity: KnowledgeArticleResponse
): IFormDataV2 => ({
  ...toFormDataV2(entity),
  tags: splitKnowledgeArticleTags(entity.tags),
});

export const MapKnowledgeArticleResponseToRequest = (
  entity: KnowledgeArticleResponse | undefined,
  values: IFormDataV2
): KnowledgeArticleRequest => {
  const tags = Array.isArray(values.tags) ? (values.tags as string[]) : [];
  return {
    id: entity?.id,
    title: (values.title as string) ?? '',
    body: (values.body as string) ?? '',
    tags: tags.length > 0 ? tags.join(', ') : undefined,
    published: Boolean(values.published),
    isGlobal: Boolean(values.isGlobal),
  };
};
